/**
 * ClientsTab.js — Token registry with account + order history
 * Extracted from AdminDashboard.js during Phase 1 refactor
 */

import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { formatCycleDate } from "../../utils/dateUtils";
import QRCode from "qrcode";
import { exportToCSV, printTable } from "../../utils/exportUtils";

export default function ClientsTab({ user }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedToken, setExpandedToken] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedTokens, setSelectedTokens] = useState(new Set());
  const [qrDataUrls, setQrDataUrls] = useState({});

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch("/api/clients", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error("Clients load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.tokenId || "").toLowerCase().includes(q) ||
      (c.account?.email || "").toLowerCase().includes(q) ||
      (c.status || "").toLowerCase().includes(q)
    );
  });

  const toggleToken = (tokenId) => {
    setSelectedTokens(prev => {
      const next = new Set(prev);
      next.has(tokenId) ? next.delete(tokenId) : next.add(tokenId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTokens.size === filtered.length) {
      setSelectedTokens(new Set());
    } else {
      setSelectedTokens(new Set(filtered.map(c => c.tokenId)));
    }
  };

  const CLIENT_COLS = [
    { key: "tokenId", label: "Token ID" },
    { key: "schoolId", label: "School ID" },
    { key: "householdSize", label: "Household Size" },
    { key: "status", label: "Status" },
    { key: "cycleEligible", label: "Cycle Eligible" },
    { key: "createdAt", label: "Created At" },
  ];

  const clientRows = () =>
    clients.map((c) => ({
      ...c,
      createdAt: c.registeredAt ? new Date(c.registeredAt).toISOString() : "",
    }));

  const exportClientsCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-clients-${today}.csv`, clientRows(), CLIENT_COLS);
  };

  const printClients = () => {
    printTable("Clients", `${clients.length} clients`, CLIENT_COLS, clientRows());
  };

  const handlePrint = async () => {
    const urls = {};
    await Promise.all(
      Array.from(selectedTokens).map(async (tokenId) => {
        try {
          urls[tokenId] = await QRCode.toDataURL(
            `https://riverwoodraiderdepot.org/order/${tokenId}`,
            { width: 220, margin: 1, errorCorrectionLevel: "H" }
          );
        } catch (err) {
          console.error(`QR generation failed for ${tokenId}:`, err);
        }
      })
    );
    setQrDataUrls(urls);
    setTimeout(() => window.print(), 300);
  };

  const STATUS_COLORS = {
    active: "#16a34a",
    inactive: "#94a3b8",
    suspended: "#dc2626",
  };
  const ORDER_STATUS_COLORS = {
    submitted: "#3b82f6",
    packed: "#8b5cf6",
    ready_for_pickup: "#f59e0b",
    picked_up: "#16a34a",
    not_picked_up: "#dc2626",
    returned_to_inventory: "#64748b",
    cancelled: "#94a3b8",
  };

  return (
    <>
      <div style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <div style={S.flexRowBetween}>
          <h2 style={S.cardTitle}>Client Registry</h2>
          <div style={S.flexRow}>
            <input
              style={{ ...S.input, width: "260px" }}
              placeholder="Search by token, email, or status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {selectedTokens.size > 0 && (
              <button
                style={{ ...S.btnPrimary, backgroundColor: "#1565c0" }}
                onClick={handlePrint}
              >
                🖨️ Print {selectedTokens.size} Selected
              </button>
            )}
            <button style={S.btnSecondary} onClick={toggleAll}>
              {selectedTokens.size === filtered.length && filtered.length > 0 ? "Deselect All" : "Select All"}
            </button>
            <button style={S.btnSecondary} onClick={exportClientsCSV} disabled={clients.length === 0}>
              Export CSV
            </button>
            <button style={S.btnSecondary} onClick={printClients} disabled={clients.length === 0}>
              Print
            </button>
            <button style={S.btnSecondary} onClick={loadClients}>
              Refresh
            </button>
          </div>
        </div>
        {!loading && filtered.length > 0 && (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ backgroundColor: "#ffffff" }}>
              <tr>
                <th style={{ ...S.th, width: "16%" }}>Token</th>
                <th style={{ ...S.th, width: "9%" }}>Status</th>
                <th style={{ ...S.th, width: "8%" }}>HH Size</th>
                <th style={{ ...S.th, width: "14%" }}>Registered</th>
                <th style={{ ...S.th, width: "22%" }}>Account</th>
                <th style={{ ...S.th, width: "8%" }}>Orders</th>
                <th style={{ ...S.th, width: "8%" }}>Print</th>
                <th style={{ ...S.th, width: "15%" }}>Actions</th>
              </tr>
            </thead>
          </table>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
        {[
          { label: "Total tokens", value: clients.length, color: "#3b82f6" },
          {
            label: "With accounts",
            value: clients.filter((c) => c.account).length,
            color: "#8b5cf6",
          },
          {
            label: "With orders",
            value: clients.filter((c) => c.orderCount > 0).length,
            color: "#16a34a",
          },
          {
            label: "No orders yet",
            value: clients.filter((c) => c.orderCount === 0).length,
            color: "#f59e0b",
          },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            style={{
              flex: 1,
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "700", color }}>
              {value}
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginTop: "2px",
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      <div style={{ backgroundColor: "#fff", padding: "20px", marginBottom: "16px" }}>
        {loading ? (
          <p style={{ color: "#64748b" }}>Loading clients...</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: "13px" }}>
            No clients found.
          </p>
        ) : (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ visibility: "hidden" }}>
              <tr>
                <th style={{ ...S.th, width: "16%" }}>Token</th>
                <th style={{ ...S.th, width: "9%" }}>Status</th>
                <th style={{ ...S.th, width: "8%" }}>HH Size</th>
                <th style={{ ...S.th, width: "14%" }}>Registered</th>
                <th style={{ ...S.th, width: "22%" }}>Account</th>
                <th style={{ ...S.th, width: "8%" }}>Orders</th>
                <th style={{ ...S.th, width: "8%" }}>Print</th>
                <th style={{ ...S.th, width: "15%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((client) => (
                <React.Fragment key={client.tokenId}>
                  <tr>
                    <td style={S.td}>
                      <strong style={{ letterSpacing: "0.5px" }}>
                        {client.tokenId}
                      </strong>
                    </td>
                    <td style={S.td}>
                      <span
                        style={S.badge(
                          STATUS_COLORS[client.status] || "#64748b",
                        )}
                      >
                        {client.status}
                      </span>
                    </td>
                    <td style={S.td}>{client.householdSize || "—"}</td>
                    <td style={S.td}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        {client.registeredAt
                          ? new Date(client.registeredAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )
                          : "—"}
                      </span>
                    </td>
                    <td style={S.td}>
                      {client.account ? (
                        <span style={{ fontSize: "12px", color: "#8b5cf6" }}>
                          {client.account.email}
                        </span>
                      ) : (
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                          Anonymous only
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      <span
                        style={{
                          fontWeight: "700",
                          color: client.orderCount > 0 ? "#16a34a" : "#94a3b8",
                        }}
                      >
                        {client.orderCount}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedTokens.has(client.tokenId)}
                        onChange={() => toggleToken(client.tokenId)}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                    </td>
                    <td style={S.td}>
                      {client.orderCount > 0 && (
                        <button
                          style={{
                            ...S.btnSecondary,
                            padding: "4px 10px",
                            fontSize: "12px",
                          }}
                          onClick={() =>
                            setExpandedToken(
                              expandedToken === client.tokenId
                                ? null
                                : client.tokenId,
                            )
                          }
                        >
                          {expandedToken === client.tokenId ? "Hide" : "View"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedToken === client.tokenId &&
                    client.orders.length > 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            padding: "0 12px 16px",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <div
                            style={{
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #e2e8f0",
                              marginTop: "4px",
                            }}
                          >
                            <table style={{ ...S.table, marginBottom: 0 }}>
                              <thead>
                                <tr style={{ backgroundColor: "#f1f5f9" }}>
                                  <th style={{ ...S.th, fontSize: "10px" }}>
                                    Cycle
                                  </th>
                                  <th style={{ ...S.th, fontSize: "10px" }}>
                                    Items
                                  </th>
                                  <th style={{ ...S.th, fontSize: "10px" }}>
                                    Status
                                  </th>
                                  <th style={{ ...S.th, fontSize: "10px" }}>
                                    Submitted
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {client.orders.map((order) => (
                                  <tr key={order.orderId}>
                                    <td style={{ ...S.td, fontSize: "12px" }}>
                                      {formatCycleDate(order.cycleId)}
                                    </td>
                                    <td style={S.td}>
                                      {(order.items || []).map((item, i) => (
                                        <span
                                          key={i}
                                          style={{
                                            fontSize: "11px",
                                            color: "#475569",
                                          }}
                                        >
                                          {item.name} ×{item.qty}
                                          {i < order.items.length - 1
                                            ? ", "
                                            : ""}
                                        </span>
                                      ))}
                                    </td>
                                    <td style={S.td}>
                                      <span
                                        style={S.badge(
                                          ORDER_STATUS_COLORS[order.status] ||
                                            "#64748b",
                                        )}
                                      >
                                        {(order.status || "submitted").replace(
                                          /_/g,
                                          " ",
                                        )}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        ...S.td,
                                        fontSize: "12px",
                                        color: "#64748b",
                                      }}
                                    >
                                      {order.submittedAt
                                        ? new Date(
                                            order.submittedAt,
                                          ).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                          })
                                        : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Printable token cards — only visible when printing */}
      {selectedTokens.size > 0 && (
        <div id="client-token-print-area">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            {filtered
              .filter(c => selectedTokens.has(c.tokenId))
              .map(client => (
                <div
                  key={client.tokenId}
                  style={{
                    width: "100%",
                    height: "5.5in",
                    border: "1px dashed #999",
                    padding: "0.4in 0.3in",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                  }}
                >
                  <div style={{
                    width: "100%",
                    backgroundColor: "#1a3a1a",
                    color: "#fff",
                    textAlign: "center",
                    padding: "10px 8px",
                    borderRadius: 6,
                  }}>
                    <div style={{ fontSize: 22, fontWeight: "bold" }}>Raider Depot</div>
                    <div style={{ fontSize: 14, marginTop: 2 }}>Riverwood International Charter School</div>
                  </div>

                  <div style={{ textAlign: "center", margin: "12px 0" }}>
                    <img
                      src={qrDataUrls[client.tokenId] || ""}
                      alt={`QR code for ${client.tokenId}`}
                      style={{ width: "2.2in", height: "2.2in" }}
                    />
                  </div>

                  <div style={{
                    fontSize: 36,
                    fontWeight: "bold",
                    fontFamily: "monospace",
                    textAlign: "center",
                    letterSpacing: 2,
                    margin: "4px 0",
                  }}>
                    {client.tokenId}
                  </div>

                  <div style={{
                    textAlign: "center",
                    fontSize: 14,
                    color: "#444",
                    lineHeight: 1.5,
                    margin: "8px 0",
                    padding: "8px 12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: 4,
                    width: "100%",
                  }}>
                    <div><strong>Can't scan the QR code?</strong></div>
                    <div>Go to: <strong>riverwoodraiderdepot.org/order</strong></div>
                    <div>Enter your code: <strong>{client.tokenId}</strong></div>
                  </div>

                  <div style={{ fontSize: 13, color: "#666", textAlign: "center" }}>
                    Need help? See your school counselor
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #client-token-print-area, #client-token-print-area * { visibility: visible; }
          #client-token-print-area {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
          }
          #client-token-print-area > div {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: 0;
            width: 100%;
            height: 100vh;
            justify-items: center;
            align-items: center;
          }
          @page { margin: 0.25in; size: letter; }
        }
      `}</style>
    </>
  );
}
